// Interactive checkbox component for toggling task completion status.
import { useRef } from 'react';
import { useTaskUI } from '@/features/tasks/context/TaskUIContext';
import { useT } from '@/i18n/useT';

const LONG_PRESS_MS = 500;

const TaskCheckbox = ({
  task,
  checked,
  onToggle,
  isExecutionView,
  hideFromTabOrder = false,
  isTouchDevice,
  isSmallScreen = false,
  onLongPress,
}) => {
  const t = useT();
  const { onBeforeToggleComplete } = useTaskUI();
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  if (isExecutionView && (isTouchDevice || isSmallScreen)) return null;

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const stopAllPropagation = (e) => {
    e.stopPropagation();
    clearLongPress();
  };

  const startLongPress = (e) => {
    e.stopPropagation();
    longPressFired.current = false;
    clearLongPress();
    if (!onLongPress) return;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onLongPress(task.id);
    }, LONG_PRESS_MS);
  };

  return (
    <button
      type="button"
      className="task-item__checkbox"
      role="checkbox"
      tabIndex={isExecutionView || hideFromTabOrder ? -1 : undefined}
      aria-label={t('taskcheckbox.markComplete', 'Mark complete')}
      aria-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        clearLongPress();
        if (longPressFired.current) {
          longPressFired.current = false;
          return;
        }
        const isChecked = !checked;
        if (isChecked) onBeforeToggleComplete?.(task.id);
        onToggle(isChecked);
      }}
      onPointerDown={startLongPress}
      onPointerUp={stopAllPropagation}
      onPointerLeave={clearLongPress}
      onMouseDown={stopAllPropagation}
      onMouseUp={stopAllPropagation}
      onTouchStart={startLongPress}
      onTouchEnd={stopAllPropagation}
    />
  );
};

export default TaskCheckbox;
